import { MenuOpenOutlined } from "@mui/icons-material"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"

import Button from "@components/button"
import LeftDrawer from "@components/persist_drawer"
import SectionBar from "@components/section_bar"
import TextInput from "@components/text_input"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import {
  EmailLogsFormData,
  useEmailLogsPageContext
} from "@contexts/main/email_logs_page_context"
import { deleteEmailAdminLogsMutation } from "@domain/email_logs"
import { getCustomers } from "@domain/customer/list"
import { Box, InputLabel, Stack } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider"
import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"

export default function EmailLogsPageLeftDrawer() {
  const {
    isLeftDrawerOpen,
    toggleIsLeftDrawerOpen,
    translate,
    setFilters,
    filters
  } = useEmailLogsPageContext()

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<EmailLogsFormData>({
      defaultValues: filters
    })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchedStartDate = watch("startDate")
  const watchedEndDate = watch("endDate")

  const [isStartDateOpen, setIsStartDateOpen] = useState(false)
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)
  const [isDateFocus, setIsDateFocus] = useState(false)

  const [searchValue, setSearchValue] = useState("")
  console.log("search value", searchValue)
  const debouncedSearch = useDebounce(searchValue, 500)

  const isSearchValid = debouncedSearch.length >= 3
  const finalSearch = isSearchValid ? debouncedSearch : ""
  console.log("final search mate", finalSearch)

  const { data: customerData, isLoading } = useQuery({
    queryKey: ["customer-data-table-email-logs", finalSearch],
    queryFn: async () => {
      console.log("is search valid mate", isSearchValid)
      console.log("final search valid mate", finalSearch)
      const response = await getCustomers({
        pageNumber: 0,
        pageSize: isSearchValid ? 25 : 1000,
        // pageSize: 1000,
        search: finalSearch
        // search: undefined
      })
      return response.data
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const onSubmit = (data: EmailLogsFormData) => {
    const isToday = data.endDate
      ? dayjs(data.endDate).isSame(dayjs(), "day")
      : false
    const finalFilters = {
      ...data,
      endDate: isToday ? new Date() : data.endDate,
      filterTrigger: Date.now()
    }
    setFilters(finalFilters)
  }

  const onDelete = async (data: EmailLogsFormData) => {
    if (!data.customerId) {
      showErrorToast(translate("customer_required") || "Customer Required")
      return
    }

    const confirm = window.confirm(
      translate("delete_email_logs_confirm") ||
        "Are you sure you want to delete email logs for the selected criteria?"
    )
    if (!confirm) return

    const response = await deleteEmailAdminLogsMutation({
      customerId: data.customerId,
      startDate: data.startDate?.getTime(),
      endDate: data.endDate?.getTime()
    })

    if (response.isSuccess) {
      showSuccessToast(
        translate("delete_success") || "Logs deleted successfully"
      )
      setFilters((prev) => ({ ...prev, filterTrigger: Date.now() }))
    } else {
      showErrorToast(response.errorMessage || "Delete failed")
    }
  }

  return (
    <LeftDrawer
      isOpen={!!isLeftDrawerOpen}
      header={<Header setClose={toggleIsLeftDrawerOpen} />}
      sx={{
        borderRight: 1,
        borderColor: "divider",
        width: "16rem",
        minWidth: "16rem",
        maxWidth: "16rem"
      }}
    >
      <Stack gap={2} p={2}>
        <TextInput
          control={control}
          name="customerId"
          placeholder={translate("select")}
          type="select"
          label={translate("customer")}
          loading={isLoading}
          onInputChange={(_, value: string, reason) => {
            if (reason === "input") {
              console.log("yes mate", value)
              setSearchValue(value)
            }
          }}
          selectOptions={[
            { value: "", label: translate("all") },
            ...(customerData?.docs
              ?.filter((item) => item.customer?.appStatus === "Enabled")
              ?.map((item) => ({
                value: item.customer?._id || "",
                label: item.customer?.companyName || ""
              }))
              .reverse() ?? [])
          ]}
        />

        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("start_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                format="DD.MM.YYYY"
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                value={watchedStartDate ? dayjs(watchedStartDate) : null}
                disableOpenPicker
                open={isStartDateOpen}
                onClose={() => setIsStartDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsStartDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("startDate", undefined)
                  }
                }}
                onChange={(date) => {
                  setValue(
                    "startDate",
                    date ? (date as unknown as dayjs.Dayjs).toDate() : undefined
                  )
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>

        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("end_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                format="DD.MM.YYYY"
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                value={watchedEndDate ? dayjs(watchedEndDate) : null}
                disableOpenPicker
                open={isEndDateOpen}
                onClose={() => setIsEndDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsEndDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("endDate", undefined)
                  }
                }}
                onChange={(date) => {
                  setValue(
                    "endDate",
                    date ? (date as unknown as dayjs.Dayjs).toDate() : undefined
                  )
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>

        <Button onClick={handleSubmit(onSubmit)}>{translate("filter")}</Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleSubmit(onDelete)}
        >
          {translate("delete_logs") || "Delete Logs"}
        </Button>
      </Stack>
    </LeftDrawer>
  )
}

function Header({ setClose }: { setClose?: () => void }) {
  const { translate } = useEmailLogsPageContext()

  return (
    <SectionBar
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        p: 1
      }}
    >
      <Typography p={1} variant="h6">
        {translate("email_logs")}
      </Typography>
      <IconButton
        disableRipple
        sx={{
          ":hover": {
            color: PRIMARY
          }
        }}
        onClick={setClose}
      >
        <MenuOpenOutlined />
      </IconButton>
    </SectionBar>
  )
}
